/**
 * AudioEngine
 *
 * Web Audio API wrapper with three buses: music, ambience, and SFX.
 * Music supports crossfade between tracks and streams from media elements;
 * everything else decodes to cached AudioBuffers. Ambience supports multiple
 * simultaneous layers. SFX are one-shot fire-and-forget.
 *
 * Must call init() after a user interaction (browser autoplay policy).
 */

export type BusName = 'master' | 'music' | 'ambience' | 'sfx' | 'voice';

interface ActiveSource {
  source: AudioBufferSourceNode;
  gain: GainNode;
}

/**
 * A streamed music track. Music runs to ten minutes a track, and a decoded
 * AudioBuffer of that is ~200MB, so music streams through a media element
 * instead of going through loadAudio and the buffer cache.
 */
interface MusicVoice {
  element: HTMLAudioElement;
  node: MediaElementAudioSourceNode;
  gain: GainNode;
  /** Removes the near-end listener. */
  detach?: () => void;
  /** Paused by pauseMusic (the element itself pauses after the fade). */
  paused: boolean;
  pauseTimer?: ReturnType<typeof setTimeout>;
}

/** Where the current music track is, for the transport UI and sync. */
export interface MusicState {
  currentTime: number;
  /** 0 until the element knows its length. */
  duration: number;
  paused: boolean;
}

export interface PlayMusicOptions {
  /** Crossfade length in seconds. */
  fadeDuration?: number;
  /** Loop the track. Defaults to true. */
  loop?: boolean;
  /** Called `endingLead` seconds before each pass through the track ends. */
  onEnding?: () => void;
  /** Seconds of warning before the end. Defaults to 4. */
  endingLead?: number;
  /** Start this many seconds in (a late joiner, a synced resume). */
  startAt?: number;
  /** Load and position the track but leave it paused (resumeMusic starts it). */
  startPaused?: boolean;
}

export class AudioEngine {
  private ctx: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private musicGain: GainNode | null = null;
  private ambienceGain: GainNode | null = null;
  private sfxGain: GainNode | null = null;
  private voiceGain: GainNode | null = null;

  // Ambience is ducked while the GM speaks. Counted rather than boolean: two
  // overlapping utterances must not have the first one to finish restore the
  // bed while the second is still talking.
  private duckCount = 0;
  private currentVoiceSource: AudioBufferSourceNode | null = null;
  /** Volume the ambience bed returns to after ducking. */
  private ambienceBaseVolume = 1;

  // Music: one track at a time with crossfade
  private currentMusic: MusicVoice | null = null;
  /** Bumped by every playMusic/stopMusic so stale loads can tell. */
  private musicRequest = 0;
  /**
   * A track that is still loading, and what the transport has asked of it
   * meanwhile. A pause or seek that arrives before the element can play is
   * recorded here and applied when it starts, instead of landing on the old
   * track that is about to fade out.
   */
  private pendingStart: { request: number; startAt: number; paused: boolean } | null = null;
  /** Transport UI subscribers; see onMusicProgress. */
  private progressListeners = new Set<(state: MusicState | null) => void>();
  private progressTimer: ReturnType<typeof setInterval> | null = null;

  // Ambience: multiple layers keyed by ID
  private ambienceLayers = new Map<string, ActiveSource>();

  // Buffer cache to avoid re-fetching
  private bufferCache = new Map<string, AudioBuffer>();

  // Loading promises to avoid duplicate fetches
  private loadingPromises = new Map<string, Promise<AudioBuffer>>();

  get isInitialized(): boolean {
    return this.ctx !== null;
  }

  /**
   * Initialize the AudioContext and wire up bus gain nodes.
   * Must be called from a user gesture (click/tap) to satisfy autoplay policy.
   */
  init(): void {
    if (this.ctx) return;

    this.ctx = new AudioContext();

    // Create bus gain nodes
    this.masterGain = this.ctx.createGain();
    this.musicGain = this.ctx.createGain();
    this.ambienceGain = this.ctx.createGain();
    this.sfxGain = this.ctx.createGain();
    this.voiceGain = this.ctx.createGain();

    // Wire: each bus → master → destination
    this.musicGain.connect(this.masterGain);
    this.ambienceGain.connect(this.masterGain);
    this.sfxGain.connect(this.masterGain);
    this.voiceGain.connect(this.masterGain);
    this.masterGain.connect(this.ctx.destination);
  }

  /**
   * Resume AudioContext if suspended (e.g., after tab becomes inactive).
   */
  async resume(): Promise<void> {
    if (this.ctx?.state === 'suspended') {
      await this.ctx.resume();
    }
  }

  /**
   * Set volume for a bus (0 to 1).
   */
  setVolume(bus: BusName, volume: number): void {
    const gain = this.getGainNode(bus);
    if (!gain || !this.ctx) return;

    const clamped = Math.max(0, Math.min(1, volume));
    // Remember where ambience should sit, so lifting a duck restores the
    // player's chosen level rather than snapping to full.
    if (bus === 'ambience') this.ambienceBaseVolume = clamped;
    gain.gain.setTargetAtTime(clamped, this.ctx.currentTime, 0.05);
  }

  /**
   * Get current volume for a bus.
   */
  getVolume(bus: BusName): number {
    const gain = this.getGainNode(bus);
    return gain?.gain.value ?? 1;
  }

  // ─── Music Bus ───────────────────────────────────────────────────────

  /**
   * Play a music track, crossfading from current if playing.
   *
   * Streams through an <audio> element routed into the music bus, so a long
   * track costs a few MB of network buffer rather than its decoded size.
   *
   * The engine has no playlist policy. A caller that wants to move on before
   * the track ends passes `onEnding`, which fires `endingLead` seconds before
   * each pass through the track finishes. Looping stays on by default even
   * then: if the caller does not answer in time (slow fetch, network error),
   * the track wraps around instead of dropping to silence.
   */
  async playMusic(url: string, options: PlayMusicOptions = {}): Promise<void> {
    if (!this.ctx || !this.musicGain) return;
    const {
      fadeDuration = 2, loop = true, onEnding, endingLead = 4, startAt = 0, startPaused = false,
    } = options;

    // A later playMusic or stopMusic while this one is still buffering wins.
    // Without this, a track that finishes loading after Stop starts anyway.
    const request = ++this.musicRequest;
    this.pendingStart = { request, startAt, paused: startPaused };

    const element = new Audio();
    // Storage serves the files cross-origin. Without CORS mode the element
    // still plays, but Web Audio hears silence from it.
    element.crossOrigin = 'anonymous';
    element.preload = 'auto';
    element.loop = loop;
    element.src = url;

    // Wait until it can play, so the crossfade does not fade into silence.
    const ready = await new Promise<boolean>((resolve) => {
      element.addEventListener('canplay', () => resolve(true), { once: true });
      element.addEventListener('error', () => resolve(false), { once: true });
    });
    const pending = this.pendingStart?.request === request ? this.pendingStart : null;
    if (pending) this.pendingStart = null;
    if (!ready) {
      console.error(`[AudioEngine] Failed to load music: ${url}`, element.error);
      releaseElement(element);
      return;
    }
    if (!pending || request !== this.musicRequest || !this.ctx || !this.musicGain) {
      releaseElement(element);
      return;
    }

    // What the transport asked for while we loaded wins over the options.
    const begin = pending.startAt;
    const beginPaused = pending.paused;
    if (begin > 0) {
      const { duration } = element;
      element.currentTime = Number.isFinite(duration) ? Math.min(begin, Math.max(0, duration - 1)) : begin;
    }

    const node = this.ctx.createMediaElementSource(element);
    const gain = this.ctx.createGain();
    node.connect(gain);
    gain.connect(this.musicGain);

    const now = this.ctx.currentTime;

    // Crossfade: fade out old, fade in new
    if (this.currentMusic) {
      this.fadeOutMusic(this.currentMusic, fadeDuration);
    }

    // Fade in new (a paused start stays silent until resumeMusic)
    gain.gain.setValueAtTime(0, now);
    if (!beginPaused) gain.gain.linearRampToValueAtTime(1, now + fadeDuration);

    const music: MusicVoice = { element, node, gain, paused: beginPaused };
    this.currentMusic = music;

    if (onEnding) {
      // Once per pass. Re-armed when a loop wraps back to the start, so a
      // caller that did not replace us hears about the next ending too.
      let warned = false;
      const onTime = () => {
        const { duration, currentTime } = element;
        if (!Number.isFinite(duration) || duration <= 0) return;
        // Never fire in the first half of a short track.
        const lead = Math.min(endingLead, duration / 2);
        const nearEnd = duration - currentTime <= lead;
        if (!nearEnd) { warned = false; return; }
        if (warned || this.currentMusic !== music) return;
        warned = true;
        onEnding();
      };
      element.addEventListener('timeupdate', onTime);
      music.detach = () => element.removeEventListener('timeupdate', onTime);
    }

    if (beginPaused) return;
    try {
      await element.play();
    } catch (err) {
      // Autoplay refused (no user gesture yet). The AudioContext has the same
      // rule, so the store only gets here after a gesture; log and move on.
      console.error(`[AudioEngine] Music did not start: ${url}`, err);
    }
  }

  /**
   * Pause the current track, holding its position. Fades out briefly first
   * so it does not click.
   */
  pauseMusic(fadeDuration = 0.25): void {
    if (this.pendingStart) this.pendingStart.paused = true;
    const music = this.currentMusic;
    if (!this.ctx || !music || music.paused) return;
    music.paused = true;

    const now = this.ctx.currentTime;
    music.gain.gain.cancelScheduledValues(now);
    music.gain.gain.setValueAtTime(music.gain.gain.value, now);
    music.gain.gain.linearRampToValueAtTime(0, now + fadeDuration);

    clearTimeout(music.pauseTimer);
    music.pauseTimer = setTimeout(() => {
      if (music.paused) music.element.pause();
    }, fadeDuration * 1000);
  }

  /** Resume from where pauseMusic left off, with a short fade in. */
  resumeMusic(fadeDuration = 0.5): void {
    // While a track loads, resume means "start it playing"; the old track is
    // on its way out and stays as it is.
    if (this.pendingStart) {
      this.pendingStart.paused = false;
      return;
    }
    const music = this.currentMusic;
    if (!this.ctx || !music || !music.paused) return;
    music.paused = false;
    clearTimeout(music.pauseTimer);

    const now = this.ctx.currentTime;
    music.gain.gain.cancelScheduledValues(now);
    music.gain.gain.setValueAtTime(music.gain.gain.value, now);
    music.gain.gain.linearRampToValueAtTime(1, now + fadeDuration);

    music.element.play().catch((err) => {
      console.error('[AudioEngine] Music did not resume', err);
    });
  }

  /** Jump the current track to `seconds`, clamped to the track. */
  seekMusic(seconds: number): void {
    if (!Number.isFinite(seconds)) return;
    if (this.pendingStart) {
      this.pendingStart.startAt = Math.max(0, seconds);
      return;
    }
    const music = this.currentMusic;
    if (!music) return;
    const { duration } = music.element;
    const max = Number.isFinite(duration) ? Math.max(0, duration - 0.25) : seconds;
    music.element.currentTime = Math.max(0, Math.min(seconds, max));
    this.emitProgress();
  }

  /** Position, length and paused state of the current track, or null. */
  getMusicState(): MusicState | null {
    // A loading track reports where it will start, so a position sent now
    // (or compared against for drift) refers to the track the user picked.
    if (this.pendingStart) {
      return { currentTime: this.pendingStart.startAt, duration: 0, paused: this.pendingStart.paused };
    }
    const music = this.currentMusic;
    if (!music) return null;
    const { currentTime, duration } = music.element;
    return {
      currentTime,
      duration: Number.isFinite(duration) ? duration : 0,
      paused: music.paused,
    };
  }

  /**
   * Subscribe to music progress at ~4Hz, plus immediately. The ticker only
   * runs while someone is listening and the tab is visible, so the UI can
   * show a progress bar without writing to the store every frame.
   */
  onMusicProgress(listener: (state: MusicState | null) => void): () => void {
    this.progressListeners.add(listener);
    listener(this.getMusicState());
    if (!this.progressTimer) {
      this.progressTimer = setInterval(() => {
        if (typeof document !== 'undefined' && document.hidden) return;
        this.emitProgress();
      }, 250);
    }
    return () => {
      this.progressListeners.delete(listener);
      if (this.progressListeners.size === 0 && this.progressTimer) {
        clearInterval(this.progressTimer);
        this.progressTimer = null;
      }
    };
  }

  private emitProgress(): void {
    if (this.progressListeners.size === 0) return;
    const state = this.getMusicState();
    for (const listener of this.progressListeners) listener(state);
  }

  /**
   * Stop current music with optional fade out.
   */
  stopMusic(fadeDuration = 1): void {
    this.musicRequest++;
    this.pendingStart = null;
    if (!this.ctx || !this.currentMusic) return;

    this.fadeOutMusic(this.currentMusic, fadeDuration);
    this.currentMusic = null;
    this.emitProgress();
  }

  private fadeOutMusic(music: MusicVoice, fadeDuration: number): void {
    if (!this.ctx) return;
    music.detach?.();
    clearTimeout(music.pauseTimer);

    const { gain } = music;
    const now = this.ctx.currentTime;

    gain.gain.cancelScheduledValues(now);
    gain.gain.setValueAtTime(gain.gain.value, now);
    gain.gain.linearRampToValueAtTime(0, now + fadeDuration);

    // Release the element after the fade so its network buffer goes too
    setTimeout(() => {
      releaseElement(music.element);
      music.node.disconnect();
      gain.disconnect();
    }, fadeDuration * 1000 + 100);
  }

  // ─── Ambience Bus ────────────────────────────────────────────────────

  /**
   * Add an ambience layer. If the layer ID already exists, does nothing.
   */
  async addAmbienceLayer(id: string, url: string, volume = 0.6): Promise<void> {
    if (!this.ctx || !this.ambienceGain) return;
    if (this.ambienceLayers.has(id)) return;

    const buffer = await this.loadAudio(url);
    if (!buffer) return;

    const gain = this.ctx.createGain();
    const source = this.ctx.createBufferSource();
    source.buffer = buffer;
    source.loop = true;
    source.connect(gain);
    gain.connect(this.ambienceGain);

    // Fade in
    const now = this.ctx.currentTime;
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(volume, now + 0.5);

    source.start(0);
    this.ambienceLayers.set(id, { source, gain });
  }

  /**
   * Remove an ambience layer with fade out.
   */
  removeAmbienceLayer(id: string, fadeDuration = 0.5): void {
    if (!this.ctx) return;

    const layer = this.ambienceLayers.get(id);
    if (!layer) return;

    const now = this.ctx.currentTime;
    layer.gain.gain.setValueAtTime(layer.gain.gain.value, now);
    layer.gain.gain.linearRampToValueAtTime(0, now + fadeDuration);

    setTimeout(() => {
      try { layer.source.stop(); } catch { /* already stopped */ }
      layer.gain.disconnect();
    }, fadeDuration * 1000 + 100);

    this.ambienceLayers.delete(id);
  }

  /**
   * Set volume for a specific ambience layer.
   */
  setAmbienceLayerVolume(id: string, volume: number): void {
    if (!this.ctx) return;
    const layer = this.ambienceLayers.get(id);
    if (!layer) return;

    const clamped = Math.max(0, Math.min(1, volume));
    layer.gain.gain.setTargetAtTime(clamped, this.ctx.currentTime, 0.05);
  }

  /**
   * Check if an ambience layer is active.
   */
  hasAmbienceLayer(id: string): boolean {
    return this.ambienceLayers.has(id);
  }

  // ─── SFX Bus ─────────────────────────────────────────────────────────

  /**
   * Play a one-shot sound effect. Fire and forget.
   */
  async playSFX(url: string, volume = 1): Promise<void> {
    if (!this.ctx || !this.sfxGain) return;

    const buffer = await this.loadAudio(url);
    if (!buffer) return;

    const gain = this.ctx.createGain();
    const source = this.ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(gain);
    gain.connect(this.sfxGain);
    gain.gain.value = volume;

    source.start(0);

    // Clean up after playback
    source.onended = () => {
      gain.disconnect();
    };
  }

  // ─── Synthesized Sounds ─────────────────────────────────────────────

  /**
   * Play a synthesized sonar ping. Two-tone descending sweep with decay.
   */
  playSynthPing(volume = 0.35): void {
    if (!this.ctx || !this.sfxGain) return;

    const now = this.ctx.currentTime;

    // Tone 1: high ping
    const osc1 = this.ctx.createOscillator();
    const gain1 = this.ctx.createGain();
    osc1.type = 'sine';
    osc1.frequency.setValueAtTime(1400, now);
    osc1.frequency.exponentialRampToValueAtTime(900, now + 0.15);
    osc1.connect(gain1);
    gain1.connect(this.sfxGain);
    gain1.gain.setValueAtTime(volume, now);
    gain1.gain.exponentialRampToValueAtTime(0.001, now + 0.4);
    osc1.start(now);
    osc1.stop(now + 0.4);

    // Tone 2: harmonic shimmer (slightly delayed)
    const osc2 = this.ctx.createOscillator();
    const gain2 = this.ctx.createGain();
    osc2.type = 'sine';
    osc2.frequency.setValueAtTime(2100, now + 0.05);
    osc2.frequency.exponentialRampToValueAtTime(1200, now + 0.25);
    osc2.connect(gain2);
    gain2.connect(this.sfxGain);
    gain2.gain.setValueAtTime(0, now);
    gain2.gain.linearRampToValueAtTime(volume * 0.5, now + 0.05);
    gain2.gain.exponentialRampToValueAtTime(0.001, now + 0.5);
    osc2.start(now);
    osc2.stop(now + 0.5);

    // Cleanup
    osc1.onended = () => gain1.disconnect();
    osc2.onended = () => gain2.disconnect();
  }

  // ─── Cleanup ─────────────────────────────────────────────────────────

  /**
   * Stop everything and close the AudioContext.
   */
  destroy(): void {
    this.stopMusic(0);
    for (const id of this.ambienceLayers.keys()) {
      this.removeAmbienceLayer(id, 0);
    }
    this.ctx?.close();
    this.ctx = null;
    this.bufferCache.clear();
    this.loadingPromises.clear();
  }

  // ─── Private helpers ─────────────────────────────────────────────────

  // ─── Voice Bus ───────────────────────────────────────────────────────

  /**
   * Play a raw PCM chunk on the voice bus and resolve when it finishes.
   *
   * Takes Float32 samples rather than a URL because Kokoro synthesises in the
   * browser — there is no file to fetch, and round-tripping through a WAV blob
   * and decodeAudioData would add latency for nothing.
   *
   * Resolving on `ended` is what lets the caller await each sentence and play
   * a narration in order while later sentences are still being synthesised.
   */
  async playVoiceChunk(samples: Float32Array, sampleRate: number): Promise<void> {
    if (!this.ctx || !this.voiceGain) return;

    const buffer = this.ctx.createBuffer(1, samples.length, sampleRate);
    // Copy through a fresh Float32Array: Kokoro's samples can be backed by a
    // SharedArrayBuffer, which copyToChannel's type signature rejects.
    buffer.copyToChannel(new Float32Array(samples), 0);

    const source = this.ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(this.voiceGain);

    this.currentVoiceSource = source;
    await new Promise<void>(resolve => {
      source.onended = () => {
        if (this.currentVoiceSource === source) this.currentVoiceSource = null;
        resolve();
      };
      source.start();
    });
  }

  /**
   * Play a synthesised narration from a URL and resolve when it finishes.
   *
   * This is the production path (EPIC-0043): the API returns a cached mp3 from
   * Storage, so there are no raw samples to hand over. Goes through the same
   * loadAudio/bufferCache machinery as music and SFX, which means a replayed
   * line is decoded once and then plays instantly.
   *
   * Sets currentVoiceSource exactly like playVoiceChunk so stopVoice() keeps
   * working unchanged for both paths.
   */
  async playVoiceUrl(url: string): Promise<void> {
    if (!this.ctx || !this.voiceGain) return;

    const buffer = await this.loadAudio(url);
    // loadAudio logs and returns null on failure. Resolving quietly is right:
    // the narration text is already on screen and is the source of truth.
    if (!buffer || !this.ctx || !this.voiceGain) return;

    const source = this.ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(this.voiceGain);

    this.currentVoiceSource = source;
    await new Promise<void>(resolve => {
      source.onended = () => {
        if (this.currentVoiceSource === source) this.currentVoiceSource = null;
        resolve();
      };
      source.start();
    });
  }

  /** Cut speech immediately — the player typed while the GM was still talking. */
  stopVoice(): void {
    try {
      this.currentVoiceSource?.stop();
    } catch {
      // Already stopped; stop() on a finished source throws and does not matter.
    }
    this.currentVoiceSource = null;
  }

  /**
   * Duck the ambience bed under speech, and lift it when the last speaker
   * finishes. Reference-counted so overlapping utterances do not restore the
   * bed early.
   */
  duckAmbience(active: boolean, duckTo = 0.25): void {
    if (!this.ctx || !this.ambienceGain) return;

    this.duckCount = Math.max(0, this.duckCount + (active ? 1 : -1));

    const target = this.duckCount > 0 ? duckTo : this.ambienceBaseVolume;
    // Slow enough to feel like mixing rather than a gate snapping shut.
    this.ambienceGain.gain.setTargetAtTime(target, this.ctx.currentTime, 0.25);
  }

  private getGainNode(bus: BusName): GainNode | null {
    switch (bus) {
      case 'master': return this.masterGain;
      case 'music': return this.musicGain;
      case 'ambience': return this.ambienceGain;
      case 'sfx': return this.sfxGain;
      case 'voice': return this.voiceGain;
    }
  }

  private async loadAudio(url: string): Promise<AudioBuffer | null> {
    if (!this.ctx) return null;

    // Return cached buffer
    const cached = this.bufferCache.get(url);
    if (cached) return cached;

    // Return in-flight promise if already loading
    const existing = this.loadingPromises.get(url);
    if (existing) return existing;

    // Fetch and decode
    const promise = (async (): Promise<AudioBuffer> => {
      const response = await fetch(url);
      const arrayBuffer = await response.arrayBuffer();
      const audioBuffer = await this.ctx!.decodeAudioData(arrayBuffer);
      this.bufferCache.set(url, audioBuffer);
      this.loadingPromises.delete(url);
      return audioBuffer;
    })();

    this.loadingPromises.set(url, promise);

    try {
      return await promise;
    } catch (err) {
      console.error(`[AudioEngine] Failed to load: ${url}`, err);
      this.loadingPromises.delete(url);
      return null;
    }
  }
}

/**
 * Stop an <audio> element and drop its source. Pausing alone keeps the
 * connection and buffered data alive; clearing src and calling load() is what
 * makes the browser let go.
 */
function releaseElement(element: HTMLAudioElement): void {
  element.pause();
  element.removeAttribute('src');
  element.load();
}

/** Singleton instance */
export const audioEngine = new AudioEngine();
